import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useState } from 'react';
import { useDebounce } from 'react-use';
import { useLibraryFilters, useSetLibraryFilters } from '@koob/trip-designer';
import { useAsync } from 'react-async';
import { getOrganizationByTo } from 'api/requestConnection';
import { getCatalogCountries } from '../../../api/countries';
import {
  KoobButton,
  KoobInputSegmented,
  KoobInputSelect,
  KoobInputSlider,
  KoobPill,
  KoobPillGroup,
  KoobSearchBar,
} from '@koob/ui';
import { getThemes } from 'api/themes';
import { getSegments } from 'api/segments';
import { getOrgUsers } from 'api/users';
import { useApp } from 'hooks';
import { useSearchParams } from 'react-router-dom';
import { KoobLocationSearch, SEARCH_TYPES } from '@koob/search';
import { FiltersBar, FiltersPopover } from '../../../components/Filters';

const MAX_TRIP_DURATION = 50;
const CITY_TYPES = [SEARCH_TYPES.CITY];

const LibraryFilter = ({ isDmcTrips, tags }) => {
  const HEIGHT = 42;
  const { t, i18n } = useTranslation(['tripDesigner', 'misc']);
  const libraryFilters = useLibraryFilters();
  const setLibraryFilters = useSetLibraryFilters();
  const [searchParams, setSearchParams] = useSearchParams();
  // The box keeps its own value; the request filter gets it 500ms after the
  // last keystroke.
  const [search, setSearch] = useState(libraryFilters?.search ?? '');
  const { data: themesData, isLoading: themesLoading } = useAsync({
    promiseFn: getThemes,
  });
  const { data: segmentsData, isLoading: segmentsLoading } = useAsync({
    promiseFn: getSegments,
  });

  const { currentOrganizationIdInt } = useApp();
  const { data: usersData, isLoading: usersLoading } = useAsync({
    promiseFn: currentOrganizationIdInt ? getOrgUsers : undefined,
    orgId: currentOrganizationIdInt,
    watch: currentOrganizationIdInt,
  });
  const users = usersData?.data || [];

  const { data, isLoading: dmcsLoading } = useAsync({
    promiseFn: getOrganizationByTo,
    withConnectionRequestType: 'template',
  });
  const dmcOptions = (data?.data || []).map(dmc => ({
    value: dmc.id,
    label: dmc?.displayName,
  }));

  const { data: countriesData } = useAsync({
    promiseFn: getCatalogCountries,
  });
  const countries = countriesData?.data || [];

  // Consumers read filter values as the legacy react-select shapes
  // ({ label, value } for dmcId/countries, api objects for
  // users/themes/segments/tags) — keep storing those and translate at the
  // select boundary.
  const themes = themesData?.data || [];
  const segments = segmentsData?.data || [];

  const updateFilterValue = useCallback(
    (property, value) => {
      setLibraryFilters(current => ({
        ...current,
        [property]: value,
      }));
      if (property === 'onlyFavorites') {
        setSearchParams({ ...searchParams, onlyFavorites: value });
      }
    },
    [setLibraryFilters],
  );

  useDebounce(
    () => {
      if ((libraryFilters?.search ?? '') !== search) {
        updateFilterValue('search', search);
      }
    },
    500,
    [search],
  );

  useEffect(() => {
    if (searchParams.has('onlyFavorites')) {
      updateFilterValue(
        'onlyFavorites',
        searchParams.get('onlyFavorites') === 'true',
      );
    }
  }, []);

  const activeFiltersCount =
    (libraryFilters?.countries?.length ?? 0) +
    (libraryFilters?.cities?.length ?? 0) +
    (libraryFilters?.tripDuration ? 1 : 0) +
    (libraryFilters?.dmcId ? 1 : 0) +
    (libraryFilters?.users ? 1 : 0) +
    (libraryFilters?.themes?.length ?? 0) +
    (libraryFilters?.segments?.length ?? 0) +
    (libraryFilters?.tags?.length ?? 0) +
    (libraryFilters?.departureLocation?.[0] ? 1 : 0) +
    (libraryFilters?.returnLocation?.[0] ? 1 : 0) +
    (libraryFilters?.guided === true || libraryFilters?.guided === false
      ? 1
      : 0);

  return (
    <FiltersBar>
      <div className="w-72 max-w-full [&_input:hover]:bg-accent">
        <KoobSearchBar
          placeholder={t('misc:searchByNameOrId')}
          value={search}
          onChange={setSearch}
        />
      </div>

      <FiltersPopover
        count={activeFiltersCount}
        onClear={() =>
          setLibraryFilters(current => ({
            ...current,
            countries: [],
            cities: [],
            tripDuration: null,
            dmcId: null,
            users: null,
            themes: [],
            segments: [],
            tags: [],
            departureLocation: [],
            returnLocation: [],
            guided: undefined,
          }))
        }
      >
        <FiltersPopover.Row>
          <FiltersPopover.Section label={t('designTripModal.form.countries')}>
            <KoobInputSelect
              placeholder={t('designTripModal.form.countries')}
              multiple
              searchable
              options={countries.map(country => ({
                value: country.id,
                label: country.name,
              }))}
              value={(libraryFilters?.countries || []).map(
                country => country.value,
              )}
              onValueChange={ids =>
                updateFilterValue(
                  'countries',
                  ids.map(id => ({
                    value: id,
                    label: countries.find(country => country.id === id)?.name,
                  })),
                )
              }
              clearable
            />
          </FiltersPopover.Section>

          <FiltersPopover.Section label={t('designTripModal.form.cities')}>
            <div className="flex flex-col gap-2">
              <KoobLocationSearch
                placeholder={t('designTripModal.form.cities')}
                types={CITY_TYPES}
                locale={i18n.language}
                onValueChange={value => {
                  if (!value) return;
                  const cities = libraryFilters?.cities || [];
                  if (cities.some(c => c.id === value.id)) return;
                  updateFilterValue('cities', [...cities, value]);
                }}
                clearOnSelect
              />

              {(libraryFilters?.cities || []).length > 0 && (
                <KoobPillGroup>
                  {(libraryFilters?.cities || []).map((city, index) => (
                    <KoobPill
                      key={city?.id ?? index}
                      onRemove={() =>
                        updateFilterValue(
                          'cities',
                          (libraryFilters?.cities || []).filter(
                            (_, i) => i !== index,
                          ),
                        )
                      }
                    >
                      {city?.title}
                    </KoobPill>
                  ))}
                </KoobPillGroup>
              )}
            </div>
          </FiltersPopover.Section>
        </FiltersPopover.Row>

        <FiltersPopover.Row>
          <FiltersPopover.Section label={t('search.dmcName')}>
            <KoobInputSelect
              placeholder={t('search.dmcName')}
              options={dmcOptions}
              value={libraryFilters?.dmcId?.value ?? ''}
              onValueChange={value =>
                updateFilterValue(
                  'dmcId',
                  value
                    ? dmcOptions.find(option => option.value === value)
                    : null,
                )
              }
              searchable
              clearable
              loading={dmcsLoading}
            />
          </FiltersPopover.Section>

          {!isDmcTrips && (
            <FiltersPopover.Section label={t('search.user')}>
              <KoobInputSelect
                placeholder={t('search.user')}
                options={users.map(user => ({
                  value: user.id,
                  label: `${user.firstName} ${user.lastName}`,
                }))}
                value={libraryFilters?.users?.id ?? ''}
                onValueChange={value =>
                  updateFilterValue(
                    'users',
                    value ? users.find(user => user.id === value) : null,
                  )
                }
                searchable
                clearable
                loading={usersLoading}
              />
            </FiltersPopover.Section>
          )}
        </FiltersPopover.Row>

        <FiltersPopover.Row>
          <FiltersPopover.Section label={t('search.experienceTheme')}>
            <KoobInputSelect
              placeholder={t('search.experienceTheme')}
              multiple
              loading={themesLoading}
              options={themes.map(item => ({
                value: item.id,
                label: item.displayName,
              }))}
              value={(libraryFilters?.themes || []).map(item => item.id)}
              onValueChange={ids =>
                updateFilterValue(
                  'themes',
                  themes.filter(item => ids.includes(item.id)),
                )
              }
              clearable
            />
          </FiltersPopover.Section>

          <FiltersPopover.Section label={t('search.experienceSegment')}>
            <KoobInputSelect
              placeholder={t('search.experienceSegment')}
              multiple
              loading={segmentsLoading}
              options={segments.map(item => ({
                value: item.id,
                label: item.displayName,
              }))}
              value={(libraryFilters?.segments || []).map(item => item.id)}
              onValueChange={ids =>
                updateFilterValue(
                  'segments',
                  segments.filter(item => ids.includes(item.id)),
                )
              }
              clearable
            />
          </FiltersPopover.Section>
        </FiltersPopover.Row>

        <FiltersPopover.Section
          label={isDmcTrips ? t('search.duration') : t('search.tripDuration')}
        >
          <KoobInputSlider
            min={0}
            max={MAX_TRIP_DURATION}
            value={[libraryFilters?.tripDuration ?? MAX_TRIP_DURATION]}
            onValueChange={([value]) =>
              updateFilterValue(
                'tripDuration',
                value === MAX_TRIP_DURATION ? null : value,
              )
            }
            formatValue={value =>
              t('search.expDurationCount', { count: value })
            }
          />
        </FiltersPopover.Section>

        {!isDmcTrips && (
          <FiltersPopover.Section label={t('library.form.tags')}>
            <KoobInputSelect
              placeholder={t('search.tripTags')}
              multiple
              options={(tags || []).map(tag => ({
                value: tag.id,
                label: tag.name,
              }))}
              value={(libraryFilters?.tags || []).map(tag => tag.id)}
              onValueChange={ids =>
                updateFilterValue(
                  'tags',
                  (tags || []).filter(tag => ids.includes(tag.id)),
                )
              }
              clearable
            />
          </FiltersPopover.Section>
        )}

        <FiltersPopover.Section label={t('misc:filters.itinerary')}>
          <FiltersPopover.Row>
            <KoobLocationSearch
              placeholder={t('designTripModal.form.departureLocation')}
              value={libraryFilters?.departureLocation?.[0]}
              types={CITY_TYPES}
              locale={i18n.language}
              onValueChange={value =>
                updateFilterValue('departureLocation', value ? [value] : [])
              }
            />
            <KoobLocationSearch
              placeholder={t('designTripModal.form.returnLocation')}
              value={libraryFilters?.returnLocation?.[0]}
              types={CITY_TYPES}
              locale={i18n.language}
              onValueChange={value =>
                updateFilterValue('returnLocation', value ? [value] : [])
              }
            />
          </FiltersPopover.Row>
        </FiltersPopover.Section>

        <FiltersPopover.Section label={t('search.guided')}>
          <KoobInputSegmented
            value={libraryFilters?.guided}
            onChange={value => updateFilterValue('guided', value)}
            options={[
              { value: undefined, label: t('search.all') },
              { value: true, label: t('search.guided') },
              { value: false, label: t('search.notGuided') },
            ]}
          />
        </FiltersPopover.Section>
      </FiltersPopover>

      {!isDmcTrips && (
        <KoobButton
          variant={libraryFilters?.onlyFavorites ? 'primary' : 'secondary'}
          style={{ height: HEIGHT, borderRadius: 9999 }}
          onClick={() =>
            updateFilterValue('onlyFavorites', !libraryFilters?.onlyFavorites)
          }
        >
          <i
            className={
              libraryFilters?.onlyFavorites
                ? 'fa-solid fa-heart'
                : 'fa-light fa-heart'
            }
            style={{ fontSize: 16 }}
          />
          <span>
            {libraryFilters?.onlyFavorites
              ? t('search.onlyFavorites')
              : t('search.withFavorites')}
          </span>
        </KoobButton>
      )}

      {libraryFilters?.type === 'template' && (
        <KoobButton
          variant={libraryFilters?.withArchived ? 'primary' : 'secondary'}
          style={{ height: HEIGHT, borderRadius: 9999 }}
          onClick={() =>
            updateFilterValue('withArchived', !libraryFilters?.withArchived)
          }
        >
          <i className="fa-light fa-box-archive" style={{ fontSize: 16 }} />
          <span>
            {libraryFilters?.withArchived
              ? t('misc:archived')
              : t('misc:noArchived')}
          </span>
        </KoobButton>
      )}
    </FiltersBar>
  );
};

export default LibraryFilter;
